import { type ChangeEvent, type FormEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MassMessageBuilding,
  MassMessageRole,
  MassMessageRolesGroup,
  MassMessageSpace,
  MassMessageSendingMethods,
} from 'models';
import type { FormikErrors } from 'formik';
import startOfToday from 'date-fns/startOfToday';
import _get from 'lodash/get';
import Box from '@mui/material/Box';

import { RecipientsBody, FormValues } from 'services/communication-messages';
import { useCooperativeId } from 'hooks';
import { Recipients } from 'containers';

import {
  Loader,
  Input,
  Checkbox,
  ValidationWarning,
  ButtonShape,
  DatePicker,
  PageTitleView,
} from 'components';
import SpacesSelectionList from 'components/spaces-selection-list';
import RolesSelectionList from 'components/roles-selection-list';

import { Dropzone, EntityType } from 'features/file-loader';

import { curryIsDisabledSendingMethod, stringCharCount } from './helpers';
import {
  useStyles,
  Counter,
  SendingMethodItem,
  Label,
  CheckboxLabel,
  DatepickerLabel,
  StyledTextarea,
  TopBar,
  ButtonsWrapper,
  Container,
  FormContainer,
  FieldWrapper,
  CheckboxContainer,
  CheckboxList,
  RecipientsContainer,
  DatepickerControlWrapper,
  SectionTitle,
  MockSectionTitle,
} from './style';

const today = startOfToday();
const MAX_DESCRIPTION_LENGTH = 160;
interface Props {
  availableSendingMethods: number[];
  buildingsList: MassMessageBuilding[];
  communicationMessageId: string | null;
  documentsToBeDeleted: string[];
  files: File[];
  errors: FormikErrors<FormValues>;
  isUploading: boolean;
  method: 'create' | 'update';
  rolesEntities: (MassMessageRole | MassMessageRolesGroup)[];
  values: FormValues;
  onAddFiles: (files: File[]) => void;
  onAddDocumentToDeletionQueue: (documentId: string) => void;
  onChangeCheckbox: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeDate: (date: Date | null, name?: string) => void;
  onChangeRoles: (roles: MassMessageRole[], checked: boolean) => void;
  onChangeSpaces: (spaces: MassMessageSpace[], checked: boolean) => void;
  onChangeTextField: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onCloseForm: () => void;
  onInitFormErrors: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const MassMessageCreateFormDetails = ({
  availableSendingMethods,
  buildingsList,
  communicationMessageId,
  documentsToBeDeleted,
  files,
  errors,
  isUploading,
  method,
  rolesEntities,
  values,
  onAddFiles,
  onAddDocumentToDeletionQueue,
  onChangeCheckbox,
  onChangeDate,
  onChangeTextField,
  onChangeRoles,
  onChangeSpaces,
  onCloseForm,
  onInitFormErrors,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const coopId = useCooperativeId();

  const recipientsBody: RecipientsBody = useMemo(
    () => ({
      communicationMessageId: null,
      coopId,
      distributingList: null,
      selectedRoles: values.selectedRoles,
      selectedSpaces: values.selectedSpaces,
      sendEmail: values.sendEmail,
      sendFloorApp: values.sendFloorApp,
      sendPortalMessage: values.sendPortalMessage,
      sendSms: values.sendSMS,
    }),
    [coopId, values]
  );

  const isDisabledSendingMethod = useMemo(
    () => curryIsDisabledSendingMethod(availableSendingMethods),
    [availableSendingMethods]
  );

  const {
    isDisabledSendEmailCheckbox,
    isDisabledSendPortalMessageCheckbox,
    isDisabledSendSMSCheckbox,
  } = useMemo(
    () => ({
      isDisabledSendEmailCheckbox: isDisabledSendingMethod(
        MassMessageSendingMethods.Email
      ),
      isDisabledSendPortalMessageCheckbox: isDisabledSendingMethod(
        MassMessageSendingMethods.PortalMessage
      ),
      isDisaledSendFloorAppCheckbox: isDisabledSendingMethod(
        MassMessageSendingMethods.FloorApp
      ),
      isDisabledSendSMSCheckbox: isDisabledSendingMethod(
        MassMessageSendingMethods.SMS
      ),
    }),
    [isDisabledSendingMethod]
  );

  const descriptionLength = useMemo(() => {
    return stringCharCount(values.description);
  }, [values.description]);

  const isExceededMaxDescriptionLength =
    values.sendSMS && descriptionLength > MAX_DESCRIPTION_LENGTH;
  const isDisabledSubmitBtn = isUploading || isExceededMaxDescriptionLength;
  const isDisabledUploadControl = values.sendSMS;
  const title =
    method === 'create' ? 'Uusi massaviesti' : 'Massaviestin muokkaus';

  return (
    <form onSubmit={onSubmit}>
      <TopBar>
        <PageTitleView title={title} />
        <ButtonsWrapper>
          <ButtonShape
            type="submit"
            className={classes.saveBtn}
            disabled={isDisabledSubmitBtn}
          >
            {t('#portal.common.button.save')}
            {isUploading && (
              <Box sx={{ marginLeft: '10px' }}>
                <Loader size={15} style={{ display: 'block' }} />
              </Box>
            )}
          </ButtonShape>
          <ButtonShape
            onClick={onCloseForm}
            className={classes.closeBtn}
            disabled={isUploading}
          >
            {t('#portal.common.button.cancel')}
          </ButtonShape>
        </ButtonsWrapper>
      </TopBar>
      <Container>
        <FormContainer>
          <FieldWrapper>
            <Label>
              {t('##attribute#uds_communicationmessage#uds_name')} <sup>*</sup>
            </Label>
            <ValidationWarning
              text={errors.subject}
              handleInitErrors={onInitFormErrors}
              Component={
                <Input
                  name="subject"
                  value={values.subject}
                  onChange={onChangeTextField}
                  disabled={isUploading}
                />
              }
            />
          </FieldWrapper>
          <FieldWrapper>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px',
              }}
            >
              <Label sx={{ margin: 0 }}>
                {t('##attribute#uds_communicationmessage#uds_description')}{' '}
                <sup>*</sup>
              </Label>
              {values.sendSMS && (
                <Counter
                  sx={{
                    color: isExceededMaxDescriptionLength
                      ? '#A51010'
                      : 'inherit',
                  }}
                >{`${descriptionLength}/${MAX_DESCRIPTION_LENGTH}`}</Counter>
              )}
            </Box>

            <ValidationWarning
              text={errors.description}
              handleInitErrors={onInitFormErrors}
              Component={
                <StyledTextarea
                  error={isExceededMaxDescriptionLength}
                  name="description"
                  value={values.description}
                  onChange={onChangeTextField}
                  disabled={isUploading}
                />
              }
            />
          </FieldWrapper>
          <Dropzone
            disabled={isDisabledUploadControl}
            value={files}
            onChange={onAddFiles}
            {...(method === 'update' && {
              documentsToBeDeleted,
              entityId: communicationMessageId,
              entityType: EntityType.MassMessage,
              onAddDocumentToDeletionQueue,
            })}
          />

          <CheckboxContainer>
            <CheckboxList sx={{ flex: 2 }}>
              <SectionTitle>Vastaanottajien valinta</SectionTitle>

              <SpacesSelectionList
                buildings={buildingsList}
                selectedSpaces={values.selectedSpaces}
                onSelectSpaces={onChangeSpaces}
              />
            </CheckboxList>
            <CheckboxList sx={{ marginLeft: '20px', flex: 2 }}>
              <MockSectionTitle />
              <ValidationWarning
                placement="top-start"
                text={errors.selectedRoles as string}
                handleInitErrors={onInitFormErrors}
                Component={
                  <RolesSelectionList
                    rolesEntities={rolesEntities}
                    selectedRoles={values.selectedRoles}
                    onSelectRoles={onChangeRoles}
                  />
                }
              />
            </CheckboxList>
            <CheckboxList sx={{ marginLeft: '20px', flex: 1 }}>
              <ValidationWarning
                text={_get(errors, 'selectedSendingMethods')}
                handleInitErrors={onInitFormErrors}
                Component={<SectionTitle>Lähetystapa *</SectionTitle>}
              />

              <SendingMethodItem>
                <Checkbox
                  name="sendEmail"
                  checked={values.sendEmail}
                  onChange={onChangeCheckbox}
                  disabled={isDisabledSendEmailCheckbox}
                />
                <CheckboxLabel>
                  {t('##attribute#uds_communicationmessage#uds_isemail')}
                </CheckboxLabel>
              </SendingMethodItem>
              <SendingMethodItem>
                <Checkbox
                  name="sendPortalMessage"
                  checked={values.sendPortalMessage}
                  onChange={onChangeCheckbox}
                  disabled={isDisabledSendPortalMessageCheckbox}
                />
                <CheckboxLabel>
                  {t(
                    '##attribute#uds_communicationmessage#uds_isportalmessage'
                  )}
                </CheckboxLabel>
              </SendingMethodItem>
              {values.sendPortalMessage && (
                <SendingMethodItem>
                  <FieldWrapper>
                    <DatepickerLabel>{'Näkyvyys päättyy'} </DatepickerLabel>
                    <DatepickerControlWrapper>
                      <DatePicker
                        cls={{
                          output: {
                            root: classes.datepicker,
                          },
                        }}
                        name="portalMessageDue"
                        date={values.portalMessageDue}
                        handleChange={onChangeDate}
                        minDate={today}
                      />
                    </DatepickerControlWrapper>
                  </FieldWrapper>
                </SendingMethodItem>
              )}
              {/* <SendingMethodItem>
                <Checkbox
                  name="sendFloorApp"
                  checked={values.sendFloorApp}
                  onChange={onChangeCheckbox}
                  disabled={isDisaledSendFloorAppCheckbox}
                />
                <CheckboxLabel>
                  {t('##attribute#uds_communicationmessage#uds_showinfloorapp')}
                </CheckboxLabel>
              </SendingMethodItem>
              {values.sendFloorApp && (
                <SendingMethodItem>
                  <FieldWrapper>
                    <DatepickerLabel>
                      Näkyvyys päättyy <sup>*</sup>
                    </DatepickerLabel>
                    <DatepickerControlWrapper>
                      <ValidationWarning
                        text={errors.floorAppMessageDue}
                        handleInitErrors={onInitFormErrors}
                        Component={
                          <DatePicker
                            cls={{
                              output: {
                                root: classes.datepicker,
                              },
                            }}
                            name="floorAppMessageDue"
                            date={values.floorAppMessageDue}
                            handleChange={onChangeDate}
                            minDate={today}
                          />
                        }
                      />
                    </DatepickerControlWrapper>
                  </FieldWrapper>
                </SendingMethodItem>
              )} */}
              <SendingMethodItem>
                <Checkbox
                  name="sendSMS"
                  checked={values.sendSMS}
                  onChange={onChangeCheckbox}
                  disabled={isDisabledSendSMSCheckbox}
                />
                <CheckboxLabel>
                  Tekstiviesti {'(Vain Kuvaus kentän tieto lähetetään)'}
                </CheckboxLabel>
              </SendingMethodItem>
            </CheckboxList>
          </CheckboxContainer>
        </FormContainer>
        <RecipientsContainer>
          <SectionTitle>Vastaanottajat</SectionTitle>

          <Recipients recipientsBody={recipientsBody} />
        </RecipientsContainer>
      </Container>
    </form>
  );
};

export default MassMessageCreateFormDetails;
